import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { InbuiltQrScannerModal } from '../components/verisure/InbuiltQrScannerModal';
import { useAuth } from '../context/AuthContext';

const SLIDES = [
  {
    id: 1,
    image: '/slides/slide1_industrial.jpg',
    badge: 'Industrial Asset Intelligence',
    title: 'Track Every Breakdown. Prove Every Repair.',
    subtitle: 'Universal digital maintenance ledger for industrial machinery, telecom infrastructure, vehicles, and electronics with tamper-proof cryptographic audit trails.',
    ctaPrimary: 'Explore Registry',
    ctaPrimaryLink: '/items',
    ctaSecondary: 'Find Technicians',
    ctaSecondaryLink: '/technicians',
  },
  {
    id: 2,
    image: '/slides/slide2_insurance.jpg',
    badge: 'Insurer Claim Verification',
    title: 'Undisputed Evidence For Insurance Claims.',
    subtitle: 'We are not an insurer. We provide the immutable photo forensics, GPS timestamps, and OEM part verification equipment owners and insurance companies need for rapid, dispute-free claim settlements.',
    ctaPrimary: 'Claims Evidence Portal',
    ctaPrimaryLink: '/claims',
    ctaSecondary: 'Read Claims Policy',
    ctaSecondaryLink: '/claims-policy',
  },
  {
    id: 3,
    image: '/slides/slide3_scanning.jpg',
    badge: 'Hardware QR Identity Badges',
    title: 'Hardware-Linked QR Identity Cards.',
    subtitle: 'Equipment size determines badge scale—from 35mm compact tools to 220mm industrial plant plates. Technicians scan in seconds to log immutable field telemetry.',
    ctaPrimary: 'QR Card Studio',
    ctaPrimaryLink: '/qr-generator',
    ctaSecondary: 'Scan Equipment QR',
    isScanTrigger: true,
  },
  {
    id: 4,
    image: '/slides/slide4_resale.jpg',
    badge: 'Secondary Market Maximizer',
    title: 'Preserve Up to +35% Resale Value.',
    subtitle: 'Verified maintenance cadence proves authentic ownership and OEM parts compliance. Prospective buyers purchase with verified confidence.',
    ctaPrimary: 'Resale Calculator',
    ctaPrimaryLink: '#calculator',
    ctaSecondary: 'About Verisure',
    ctaSecondaryLink: '/about',
  },
];

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  // Interactive Resale Calculator State
  const [calcCategory, setCalcCategory] = useState<'industrial' | 'vehicle' | 'telecom' | 'electronics'>('industrial');
  const [calcPrice, setCalcPrice] = useState<number>(8500000);
  const [calcYears, setCalcYears] = useState<number>(3);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 6500);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);

  // Dynamic Calculator formulas
  const calculateRetention = () => {
    const depRates = { industrial: 0.08, vehicle: 0.14, telecom: 0.10, electronics: 0.18 };
    const rate = depRates[calcCategory];
    const totalDep = Math.min(0.75, calcYears * rate);
    const baseValue = calcPrice * (1 - totalDep);

    // With Verisure verified maintenance history (+28% retention bonus)
    const verisureValue = Math.round(baseValue * 1.28);
    const paperlessValue = Math.round(baseValue * 0.82);
    const premiumDifference = verisureValue - paperlessValue;

    return { verisureValue, paperlessValue, premiumDifference };
  };

  const { verisureValue, paperlessValue, premiumDifference } = calculateRetention();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans selection:bg-orange-500 selection:text-white">
      {/* =========================================================================
          TOP NAVIGATION
      ========================================================================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 py-3.5 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/official.png" alt="Verisure" className="h-9 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-300">
            <Link to="/items" className="hover:text-orange-400 transition">Asset Registry</Link>
            <Link to="/technicians" className="hover:text-orange-400 transition">
              Find Technicians
            </Link>
            <Link to="/qr-generator" className="hover:text-orange-400 transition">QR Studio</Link>
            <Link to="/claims" className="hover:text-orange-400 transition">Insurance Claims</Link>
            <Link to="/about" className="hover:text-orange-400 transition">About Us</Link>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsScannerOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-orange-400 border border-orange-500/30 text-xs font-bold px-3.5 py-2 rounded-xl flex items-center gap-1.5 transition"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              Scan QR
            </button>
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg shadow-orange-500/25 transition flex items-center gap-1.5"
              >
                <span>Dashboard</span>
                <span>&rarr;</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg shadow-orange-500/25 transition"
              >
                Portal Login
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* =========================================================================
          HERO IMAGE SLIDESHOW
      ========================================================================== */}
      <section className="relative pt-24 min-h-[90vh] flex items-center overflow-hidden">
        {/* Slide Background Images */}
        {SLIDES.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105 pointer-events-none'
            }`}
            style={{ transition: 'opacity 1s ease-in-out, transform 8s ease-out' }}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-center"
            />
            {/* Cinematic Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/75 to-slate-950/40"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent"></div>
          </div>
        ))}

        {/* Slideshow Content Container */}
        <div className="container mx-auto px-4 py-16 relative z-20 max-w-6xl">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-orange-500/15 border border-orange-500/30 text-orange-400 text-[11px] font-extrabold uppercase tracking-widest rounded-full backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>{SLIDES[currentSlide].badge}</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight tracking-tight drop-shadow-lg">
              {SLIDES[currentSlide].title}
            </h1>

            <p className="text-slate-300 text-base md:text-lg leading-relaxed drop-shadow">
              {SLIDES[currentSlide].subtitle}
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  const link = SLIDES[currentSlide].ctaPrimaryLink;
                  if (link.startsWith('#')) {
                    document.getElementById(link.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    navigate(link);
                  }
                }}
                className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-extrabold px-7 py-3.5 rounded-2xl shadow-xl shadow-orange-500/30 hover:opacity-95 transition text-sm flex items-center gap-2"
              >
                {SLIDES[currentSlide].ctaPrimary} &rarr;
              </button>

              {SLIDES[currentSlide].isScanTrigger ? (
                <button
                  type="button"
                  onClick={() => setIsScannerOpen(true)}
                  className="bg-slate-900/80 hover:bg-slate-800 text-slate-200 border border-slate-700 font-bold px-6 py-3.5 rounded-2xl backdrop-blur-md transition text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <rect x="7" y="7" width="3" height="3"/>
                    <rect x="14" y="7" width="3" height="3"/>
                    <rect x="7" y="14" width="3" height="3"/>
                    <rect x="14" y="14" width="3" height="3"/>
                  </svg>
                  {SLIDES[currentSlide].ctaSecondary}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const target = SLIDES[currentSlide].ctaSecondaryLink;
                    if (target) {
                      if (target.startsWith('#')) {
                        document.getElementById(target.substring(1))?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate(target);
                      }
                    }
                  }}
                  className="bg-slate-900/80 hover:bg-slate-800 text-slate-200 border border-slate-700 font-bold px-6 py-3.5 rounded-2xl backdrop-blur-md transition text-sm"
                >
                  {SLIDES[currentSlide].ctaSecondary}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Slideshow Controls (Arrows & Dots) */}
        <div className="absolute bottom-8 right-6 md:right-12 z-20 flex items-center gap-3">
          <button
            type="button"
            onClick={prevSlide}
            className="w-10 h-10 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700 text-white flex items-center justify-center transition backdrop-blur-md"
            title="Previous Slide"
          >
            &#8592;
          </button>
          
          <div className="flex gap-2">
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentSlide(idx)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentSlide ? 'w-8 bg-orange-500' : 'w-2.5 bg-slate-700 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={nextSlide}
            className="w-10 h-10 rounded-full bg-slate-900/80 hover:bg-slate-800 border border-slate-700 text-white flex items-center justify-center transition backdrop-blur-md"
            title="Next Slide"
          >
            &#8594;
          </button>
        </div>
      </section>

      {/* =========================================================================
          KEY VALUE PILLARS & STATS
      ========================================================================== */}
      <section className="py-12 border-y border-slate-800 bg-slate-900/40">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-400 to-amber-400 font-mono">
                ₦8.5M+
              </div>
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">
                Asset Value Tracked
              </div>
            </div>

            <div>
              <div className="text-3xl md:text-4xl font-black text-white font-mono">
                150+
              </div>
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">
                Verified Repair Logs
              </div>
            </div>

            <div>
              <div className="text-3xl md:text-4xl font-black text-emerald-400 font-mono">
                99.2%
              </div>
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">
                Dispute-Free Claims
              </div>
            </div>

            <div>
              <div className="text-3xl md:text-4xl font-black text-amber-400 font-mono">
                35+
              </div>
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mt-1">
                Vetted Field Engineers
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          FREEMIUM MODEL SHOWCASE SECTION
      ========================================================================== */}
      <section className="py-16 bg-gradient-to-b from-slate-950 via-slate-900/60 to-slate-950 border-b border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs font-black uppercase tracking-wider mb-3">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>100% Free To Start • No Payment Required</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
              Start Protecting Equipment For Free
            </h2>
            <p className="text-slate-400 text-sm md:text-base mt-3 leading-relaxed">
              Everyone can register and use Verisure without paying a dime. Get free cryptographic hardware QR codes right out of the box, and only upgrade when your operations scale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Individual Free Tier */}
            <div className="glass-card rounded-3xl p-6 md:p-8 border border-white/[0.08] hover:border-orange-500/40 transition flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-orange-400 bg-orange-500/10 px-3 py-1 rounded-full border border-orange-500/20">
                    Individuals
                  </span>
                  <span className="text-xs font-bold text-emerald-400">Forever Free</span>
                </div>
                <div className="text-3xl font-black text-white mb-1">
                  5 <span className="text-sm font-normal text-slate-400">Free QR Codes</span>
                </div>
                <p className="text-xs text-slate-400 mb-6">
                  Perfect for homeowners, car owners, freelancers, and personal electronic device protection.
                </p>
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Up to 5 Digital QR Identity Badges
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Immutable Repair &amp; Service Ledger
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Real-Time Resale Valuation
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Instant Zero-Cost Registration
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-white/[0.08]">
                <Link
                  to="/login?type=owner"
                  className="w-full block text-center py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs transition"
                >
                  Register Free (5 Codes) &rarr;
                </Link>
              </div>
            </div>

            {/* Organizations Free Tier */}
            <div className="glass-card rounded-3xl p-6 md:p-8 border border-orange-500/40 bg-gradient-to-b from-orange-500/10 to-transparent transition flex flex-col justify-between relative shadow-xl shadow-orange-500/10">
              <span className="absolute -top-3 right-6 bg-gradient-to-r from-red-600 to-orange-500 text-white text-[10px] font-black px-3 py-0.5 rounded-full uppercase tracking-wider shadow">
                Popular
              </span>
              <div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-orange-400 bg-orange-500/20 px-3 py-1 rounded-full border border-orange-500/30">
                    Organizations
                  </span>
                  <span className="text-xs font-bold text-emerald-400">Free Starter</span>
                </div>
                <div className="text-3xl font-black text-white mb-1">
                  10 <span className="text-sm font-normal text-slate-400">Free QR Codes</span>
                </div>
                <p className="text-xs text-slate-300 mb-6">
                  For telecom stations, data centers, hospitals, factories, and corporate fleets.
                </p>
                <ul className="space-y-2.5 text-xs text-slate-200">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Up to 10 Certified QR Codes Free
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Multi-Technician Service Logging
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Outlet Blank Tag Management
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span> Claims Evidence Pre-Audit
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-white/[0.08]">
                <Link
                  to="/login?type=company"
                  className="w-full block text-center py-3 rounded-2xl bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-extrabold text-xs shadow-lg shadow-orange-500/25 transition"
                >
                  Register Organization (10 Codes Free) &rarr;
                </Link>
              </div>
            </div>

            {/* Premium Scaling Tier */}
            <div className="glass-card rounded-3xl p-6 md:p-8 border border-white/[0.08] hover:border-amber-500/40 transition flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-black uppercase tracking-wider text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                    Enterprise Pro
                  </span>
                  <span className="text-xs font-bold text-slate-400">Paid Scaling</span>
                </div>
                <div className="text-3xl font-black text-white mb-1">
                  Unlimited <span className="text-sm font-normal text-slate-400">QR Codes</span>
                </div>
                <p className="text-xs text-slate-400 mb-6">
                  When you need more than 5 (individuals) or 10 (companies) codes for your operations.
                </p>
                <ul className="space-y-2.5 text-xs text-slate-300">
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400 font-bold">★</span> Zero Limits on Hardware QR Badges
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400 font-bold">★</span> Bulk Sheet Vinyl Printing Studio
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400 font-bold">★</span> Multi-Site Role-Based Tenant Control
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-400 font-bold">★</span> Priority SLA &amp; Claims Support
                  </li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-white/[0.08]">
                <Link
                  to="/login"
                  className="w-full block text-center py-3 rounded-2xl bg-slate-900 border border-slate-700 hover:border-amber-500/50 text-slate-200 hover:text-white font-bold text-xs transition"
                >
                  View Plans &amp; Pricing &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          THE CORE ECOSYSTEM ARCHITECTURE
      ========================================================================== */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4 max-w-6xl space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full">
              Enterprise Lifecycle Management
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">
              Designed For Equipment Owners, Insurers & Technicians
            </h2>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed">
              Verisure is a software evidence network that guarantees trust at every phase of machinery operation, breakdown, and ownership transfer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Equipment Owners */}
            <div className="bg-slate-900 border border-slate-800 hover:border-orange-500/40 p-8 rounded-3xl transition duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400 mb-6">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition">
                  For Equipment Owners
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Eliminate lost paper maintenance records. Track real-time condition scores, prevent improper servicing, and preserve up to +35% resale market value.
                </p>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> Hardware-linked QR Identity Badges</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> Instant breakdown claim evidence export</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> On-demand access to verified technicians</li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-800">
                <Link to="/items" className="text-orange-400 text-xs font-bold hover:text-orange-300 flex items-center gap-1">
                  Explore Registry Features &rarr;
                </Link>
              </div>
            </div>

            {/* Card 2: Insurance Companies */}
            <div className="bg-slate-900 border border-slate-800 hover:border-orange-500/40 p-8 rounded-3xl transition duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400 mb-6">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition">
                  For Insurance Companies
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Cut claim processing time from weeks to seconds. Audit legitimate historical servicing, inspect before/after photos, and combat breakdown fraud.
                </p>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> Tamper-proof photographic repair forensics</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> GPS on-site inspection stamping</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> OEM vs aftermarket part verification</li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-800">
                <Link to="/claims-policy" className="text-orange-400 text-xs font-bold hover:text-orange-300 flex items-center gap-1">
                  View Insurer Audit Protocols &rarr;
                </Link>
              </div>
            </div>

            {/* Card 3: Certified Technicians */}
            <div className="bg-slate-900 border border-slate-800 hover:border-orange-500/40 p-8 rounded-3xl transition duration-300 flex flex-col justify-between group">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-6">
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition">
                  For Certified Technicians
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Build a verified public track record. Showcase maintained equipment portfolios, receive high-trust service requests, and get rated transparently.
                </p>
                <ul className="space-y-2 text-xs text-slate-300">
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> Mobile PWA quick-scan repair logging</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> KYC Verified Master Specialist Badge</li>
                  <li className="flex items-center gap-2"><span className="text-orange-400 font-bold">✓</span> Direct client booking marketplace</li>
                </ul>
              </div>
              <div className="mt-8 pt-4 border-t border-slate-800">
                <Link to="/technicians" className="text-orange-400 text-xs font-bold hover:text-orange-300 flex items-center gap-1">
                  Join Technician Network &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          INTERACTIVE RESALE & RETENTION CALCULATOR
      ========================================================================== */}
      <section id="calculator" className="py-20 bg-slate-900/60 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full">
              Dynamic Valuation Engine
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              Calculate Your Verified Resale Premium
            </h2>
            <p className="text-slate-400 text-sm">
              See how verified maintenance history directly protects your capital investment over time.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-10 shadow-2xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Inputs */}
            <div className="lg:col-span-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  1. Equipment Category
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'industrial', label: 'Industrial & Genset' },
                    { key: 'vehicle', label: 'Vehicle & Fleet' },
                    { key: 'telecom', label: 'Telecom & Optical' },
                    { key: 'electronics', label: 'Electronics & IT' },
                  ].map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => setCalcCategory(cat.key as any)}
                      className={`px-3 py-2.5 rounded-xl text-xs font-bold transition text-left border ${
                        calcCategory === cat.key
                          ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white border-orange-400'
                          : 'bg-slate-950 text-slate-300 border-slate-800 hover:bg-slate-800'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  2. Original Purchase Price (₦)
                </label>
                <input
                  type="number"
                  value={calcPrice}
                  onChange={(e) => setCalcPrice(Number(e.target.value) || 0)}
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500 font-mono"
                />
              </div>

              <div>
                <div className="flex justify-between text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  <span>3. Equipment Age / Service Period</span>
                  <span className="text-orange-400">{calcYears} Years</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={calcYears}
                  onChange={(e) => setCalcYears(Number(e.target.value))}
                  className="w-full accent-orange-500 cursor-pointer"
                />
              </div>
            </div>

            {/* Right Output Results Card */}
            <div className="lg:col-span-6 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-6">
              <div className="border-b border-slate-800 pb-4">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">
                  Verisure Verified Valuation
                </div>
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-orange-400 to-amber-400 font-mono mt-1">
                  ₦{verisureValue.toLocaleString()}
                </div>
                <div className="text-xs text-emerald-400 font-bold mt-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span>Retains +{Math.round((verisureValue / calcPrice) * 100)}% of original MSRP</span>
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex justify-between items-center text-slate-400">
                  <span>Unverified Market Peer Baseline:</span>
                  <span className="font-mono text-slate-300">₦{paperlessValue.toLocaleString()}</span>
                </div>

                <div className="flex justify-between items-center text-slate-400">
                  <span>Verisure Verified Maintenance Gain:</span>
                  <span className="font-mono text-emerald-400 font-bold">+₦{premiumDifference.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl text-xs text-emerald-300 leading-relaxed">
                <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400 mb-1">Resale Advantage</div>
                Buyers pay an extra <strong>₦{premiumDifference.toLocaleString()}</strong> on this asset because its immutable maintenance records and OEM parts are verified.
              </div>

              <Link
                to="/items"
                className="block text-center bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold py-3 rounded-xl transition text-xs"
              >
                Register & Tag This Asset Now &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          ADAPTIVE QR SIZING PREVIEW
      ========================================================================== */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4 max-w-6xl space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full">
              Precision Form Factors
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-white">
              Equipment-Adaptive QR Identity Cards
            </h2>
            <p className="text-slate-400 text-sm md:text-base">
              One size does not fit all. Verisure formats and scales tags for micro-tools up to heavy industrial plant installations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl text-center space-y-3 hover:border-orange-500/40 transition flex flex-col items-center">
              <div className="text-xs font-black text-orange-400 uppercase tracking-wider">Compact (35–45mm)</div>
              <div className="w-16 h-16 bg-white/[0.04] border border-white/10 rounded-2xl flex items-center justify-center text-orange-400 my-2">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base">Handheld & Sensors</h4>
              <p className="text-slate-400 text-xs">Smartphones, optical meters, circuit components, and field power tools.</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl text-center space-y-3 hover:border-orange-500/40 transition flex flex-col items-center">
              <div className="text-xs font-black text-orange-400 uppercase tracking-wider">Standard (75–85mm)</div>
              <div className="w-16 h-16 bg-white/[0.04] border border-white/10 rounded-2xl flex items-center justify-center text-orange-400 my-2">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base">Appliances & PCs</h4>
              <p className="text-slate-400 text-xs">Laptops, diagnostic test benches, household HVAC, and split systems.</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl text-center space-y-3 hover:border-orange-500/40 transition flex flex-col items-center">
              <div className="text-xs font-black text-orange-400 uppercase tracking-wider">Large (120–140mm)</div>
              <div className="w-16 h-16 bg-white/[0.04] border border-white/10 rounded-2xl flex items-center justify-center text-orange-400 my-2">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base">Vehicles & Cabinets</h4>
              <p className="text-slate-400 text-xs">Commercial delivery fleets, telecom server racks, and utility trailers.</p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl text-center space-y-3 hover:border-orange-500/40 transition flex flex-col items-center">
              <div className="text-xs font-black text-orange-400 uppercase tracking-wider">Industrial (180–220mm)</div>
              <div className="w-16 h-16 bg-white/[0.04] border border-white/10 rounded-2xl flex items-center justify-center text-orange-400 my-2">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
                  <path d="M18 18h.01"/>
                  <path d="M14 18h.01"/>
                  <path d="M10 18h.01"/>
                </svg>
              </div>
              <h4 className="font-bold text-white text-base">Plant Machinery</h4>
              <p className="text-slate-400 text-xs">Caterpillar gensets, power sub-stations, cranes, and heavy industrial turbines.</p>
            </div>
          </div>

          <div className="text-center pt-4">
            <Link
              to="/qr-generator"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-bold px-8 py-3.5 rounded-2xl shadow-lg shadow-orange-500/25 hover:opacity-95 transition text-sm"
            >
              Open QR Card Generator Studio &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================================================
          TESTIMONIALS & TRUST
      ========================================================================== */}
      <section className="py-20 bg-slate-900/30 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl space-y-12">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-black text-white">Verified by Industry Leaders</h2>
            <p className="text-slate-400 text-sm">Hear from facility directors, insurance adjusters, and fleet engineers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-950 border border-slate-800 p-6 rounded-3xl space-y-4">
              <div className="flex text-amber-400">★★★★★</div>
              <p className="text-slate-300 text-xs leading-relaxed italic">
                "Our facility operates five 500kVA backup gensets. Verisure made our recent alternator failure claim with Leadway Insurance settle in 48 hours because our complete repair history was digitally verified."
              </p>
              <div className="pt-2 border-t border-slate-800">
                <div className="font-bold text-white text-xs">Engr. Chidi Okonkwo</div>
                <div className="text-slate-500 text-[11px]">Director of Facilities, Apex Agro</div>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800 p-6 rounded-3xl space-y-4">
              <div className="flex text-amber-400">★★★★★</div>
              <p className="text-slate-300 text-xs leading-relaxed italic">
                "As an insurance claims investigator, Verisure is revolutionary. Scanning the equipment QR shows me before/after photos and genuine part invoices instantly. Zero ambiguity, zero fraud."
              </p>
              <div className="pt-2 border-t border-slate-800">
                <div className="font-bold text-white text-xs">Sarah Jenkins (CLAM)</div>
                <div className="text-slate-500 text-[11px]">Senior Property & Casualty Loss Adjuster</div>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800 p-6 rounded-3xl space-y-4">
              <div className="flex text-amber-400">★★★★★</div>
              <p className="text-slate-300 text-xs leading-relaxed italic">
                "When we sold our commercial fleet trucks, having the Verisure QR Card and verifiable maintenance records got us 25% higher resale valuation over market averages."
              </p>
              <div className="pt-2 border-t border-slate-800">
                <div className="font-bold text-white text-xs">Tunde Bakare</div>
                <div className="text-slate-500 text-[11px]">Head of Logistics, Transit Global</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          WORLD-CLASS COMPREHENSIVE FOOTER
      ========================================================================== */}
      <footer className="border-t border-slate-800 bg-slate-950 py-16 text-slate-400 text-xs">
        <div className="container mx-auto px-4 max-w-6xl grid grid-cols-1 md:grid-cols-5 gap-8 mb-12">
          {/* Col 1: Brand */}
          <div className="md:col-span-2 space-y-4">
            <img src="/official.png" alt="Verisure" className="h-10 w-auto object-contain" />
            <p className="text-slate-400 text-xs leading-relaxed max-w-sm">
              Verisure is the world’s leading digital maintenance ledger and evidence platform. We help equipment owners track breakdowns, prove servicing for insurance claims, and preserve resale asset value.
            </p>
            <div className="text-slate-500 text-[11px]">
              Built with Cryptographic Immutability & Hardware QR Integration
            </div>
          </div>

          {/* Col 2: Platform */}
          <div className="space-y-3">
            <h4 className="font-bold text-white uppercase tracking-wider text-xs">Platform</h4>
            <ul className="space-y-2">
              <li><Link to="/items" className="hover:text-orange-400 transition">Asset Registry</Link></li>
              <li><Link to="/qr-generator" className="hover:text-orange-400 transition">QR Studio</Link></li>
              <li><Link to="/technicians" className="hover:text-orange-400 transition">Find Technicians</Link></li>
              <li><Link to="/claims" className="hover:text-orange-400 transition">Claims Evidence Portal</Link></li>
              <li><Link to="/login" className="hover:text-orange-400 transition">Engineer Portal</Link></li>
            </ul>
          </div>

          {/* Col 3: Company */}
          <div className="space-y-3">
            <h4 className="font-bold text-white uppercase tracking-wider text-xs">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="hover:text-orange-400 transition">About Us</Link></li>
              <li><Link to="/technicians" className="hover:text-orange-400 transition">Certified Network</Link></li>
              <li><a href="#calculator" className="hover:text-orange-400 transition">Resale Engine</a></li>
              <li><Link to="/login" className="hover:text-orange-400 transition">Admin Console</Link></li>
            </ul>
          </div>

          {/* Col 4: Legal & Policies */}
          <div className="space-y-3">
            <h4 className="font-bold text-white uppercase tracking-wider text-xs">Policies & Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terms" className="hover:text-orange-400 transition font-semibold text-slate-300">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-orange-400 transition">Privacy & Data Policy</Link></li>
              <li><Link to="/claims-policy" className="hover:text-orange-400 transition">Insurance Claims Policy</Link></li>
              <li><Link to="/technician-policy" className="hover:text-orange-400 transition">Technician Code of Conduct</Link></li>
            </ul>
          </div>
        </div>

        <div className="container mx-auto px-4 max-w-6xl pt-8 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-600 text-[11px]">
          <div>
            &copy; {new Date().getFullYear()} VERISURE PLATFORM. All rights reserved. Verisure is an independent maintenance evidence software platform.
          </div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-slate-400">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-400">Privacy</Link>
            <Link to="/claims-policy" className="hover:text-slate-400">Evidence Standards</Link>
          </div>
        </div>
      </footer>

      {/* Inbuilt Interactive QR Scanner */}
      <InbuiltQrScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
      />
    </div>
  );
};
